/**
 * Lightweight singleton for lightbox state — any component can call
 * lightboxStore.open() without prop-drilling through the message tree.
 * Pattern mirrors chatStore's closure-based approach.
 */
import { useEffect, useState } from 'react';

export interface LightboxItem {
  /** URL to display/stream the resource (served via local API). */
  url: string;
  /** Human-readable file name shown in the lightbox header. */
  fileName: string;
  /** MIME type drives which player/renderer is used. */
  mimeType: string;
  /** Optional separate URL for downloading (defaults to `url`). */
  downloadUrl?: string;
}

type Listener = (item: LightboxItem | null) => void;
const listeners = new Set<Listener>();
let current: LightboxItem | null = null;

export const lightboxStore = {
  open(item: LightboxItem): void {
    current = item;
    listeners.forEach((l) => l(current));
  },
  close(): void {
    current = null;
    listeners.forEach((l) => l(null));
  },
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  getCurrent(): LightboxItem | null {
    return current;
  },
};

/** Hook — returns current item and a close fn. Subscribes/unsubscribes automatically. */
export function useLightboxItem(): { item: LightboxItem | null; close: () => void } {
  const [item, setItem] = useState<LightboxItem | null>(lightboxStore.getCurrent());
  useEffect(() => lightboxStore.subscribe(setItem), []);
  return { item, close: lightboxStore.close };
}
